import React, { useState, useEffect, useRef } from 'react';
import {
  useParams, useLocation, useNavigate, Outlet,
} from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap5';

import routes from 'app/routes';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import useStep from 'components/Common/Members/Hooks/useStep';
import Calendar from 'components/Common/Coaches/Widgets/Calendar/Index';
import ProgramInfo from 'components/Members/Schedule/Widgets/ProgramInfo/Index';
import BackButton from 'components/Members/Schedule/Widgets/Scheduling/BackButton';

import Actions from 'components/Members/Schedule/Actions';

import Alert from 'components/new_ui/Alerts/alert';
import confirmAlert from 'components/new_ui/Alerts/confirm';

import confirmWithoutCancel from 'components/new_ui/Alerts/confirmWithoutCancel';

import { useContext as useUsersContext } from 'components/Users/Context';

import { useContext as useHomeContext } from 'components/Members/Home/Context';
import Breadcrumbs from 'components/new_ui/Breadcrumbs/Index';

import useSize from 'components/Common/Screen/Hooks/useSize';

export default function Index() {
  const navigate = useNavigate();

  const { actions: userActions, dispatch: userDispatch } = useUsersContext();

  const { state: stateLocation } = useLocation();
  const { stepId } = useParams();

  const step = useStep(stepId);
  const [source] = useState(stateLocation?.source || 'sessions');
  const [sourceId] = useState(stateLocation?.sourceId || null);
  const [session, setSession] = useState(stateLocation?.session || null);

  const [selectedTime, setSelectedTime] = useState(stateLocation?.selectedTime || null);

  const [loading, setLoading] = useState(false);

  const isCancel = session?.attributes?.status?.includes('Undelivered');
  const isWaiting = session?.attributes?.status?.includes('Waiting');
  const isCompleted = session?.attributes?.status?.includes('Completed');

  const isPossibleScheduleReschedule = step?.attributes?.actions.some(
    (item) => item.action === 'reschedule' || item.action === 'schedule',
  );

  const wrapper = useRef(null);
  const size = useSize(wrapper);

  const homeContext = useHomeContext();

  const breadcrumbs = [
    {
      name: 'Home',
      path: '/members/home',
    },
    {
      name: 'Schedule',
    },
  ];

  useEffect(() => {
    if (step?.attributes?.session_id) {
      if (!session) {
        Actions.getSession(step, (data) => {
          setSelectedTime(data.attributes.start_at_calendar_format);
          setSession(data);
        });
      }
    }
  }, [step]);

  useEffect(() => {
    if (step && !step.attributes.session_id) {
      Actions.possibilitySchedule(step.attributes.program.id, (data) => {
        if (!data.possibility) {
          navigate(routes.members.steps.buyOrSkipSchedule(step.id), {
            state: {
              isPossibleSkip: data.isPossibleSkip,
              isScheduleSessionPage: true,
              reason: data.reason,
            },
          });
        }
      });
    }
  }, [step]);

  const navigateNext = (obj) => {
    navigate(routes.members.steps.scheduleSummary(step.id), {
      state: {
        step,
        source,
        sourceId,
        selectedTime,
        session: obj,
      },
    });
  };

  useEffect(() => {
    if (step) {
      if (isCancel || isWaiting || isCompleted || !isPossibleScheduleReschedule) {
        navigateNext(session);
      }
    }
  }, [step]);

  const prepareSession = () => {
    const params = {
      start_at: selectedTime,
      zoom_id: session?.attributes?.zoom_id,
    };

    setLoading(true);

    Actions.prepareSession(step, params, (data) => {
      setLoading(false);
      navigateNext(data);
    }, (errors) => {
      setLoading(false);
      Alert(
        {
          title: 'Error',
          text: errors.errors[0].detail,
          confirmButtonText: 'Close',
        },
      );
    });
  };

  const rescheduleSession = () => {
    setLoading(true);

    const params = {
      start_at: selectedTime,
      zoom_id: session?.attributes?.zoom_id,
    };

    let url = routes.members.schedule.index;
    if (source === 'program') {
      url = routes.members.programs.show(sourceId);
    }

    if (isCancel || isWaiting || isCompleted) {
      document.location.href = url;
      return;
    }

    const processError = (errors) => {
      setLoading(false);
      Alert(
        {
          title: 'Unable to Reschedule',
          text: errors.errors[0].detail,
          confirmButtonText: 'OK',
        },
      );
    };

    Actions.reschedule(step, params, () => {
      setLoading(false);

      confirmWithoutCancel({
        title: 'Reschedule Session',
        text: `You have successfully rescheduled your session. 
          Your coach will be notified about the change.`,
        confirmButtonText: 'Close',
      }, () => {
        document.location.href = url;
      });
    }, (errors) => {
      processError(errors);
    });
  };

  const onClickSessionCancel = () => {
    Actions.cancelType(step, (data) => {
      let text = 'Do you want to cancel this session? The session will disappear from your Calendar.';

      if (data.type === 'smaller_24_hours') {
        text = 'Less then 24 hours to start. Do you really want to cancel the session?';
      }

      confirmAlert({
        title: 'Cancel Session',
        text,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }, () => {
        setLoading(true);
        Actions.cancel(step, (sessionData) => {
          setLoading(false);
          userActions.load(userDispatch);
          navigateNext(sessionData);
        }, (errors) => {
          setLoading(false);
          Alert(
            {
              title: 'Error',
              text: errors.errors[0].detail,
              confirmButtonText: 'Close',
            },
          );
        });
      });
    });
  };

  return (
    <div ref={wrapper}>
      {(!step || (step?.attributes?.session_id && !session))
        ? (
          <div className="content">
            <section>
              <LoadingPlaceholder text="Loading..." />
            </section>
          </div>
        )

        : (
          <div className="content">
            {homeContext && <Breadcrumbs breadcrumbs={breadcrumbs} />}
            <section>
              <h3>
                Session:
                {' '}
                {step.attributes.title}
              </h3>
            </section>

            <section>
              <Row>
                <Col lg={size?.width < 1024 ? 12 : 6}>
                  <ProgramInfo record={step} session={session} />
                </Col>

                <Col lg={size?.width < 1024 ? 12 : 6}>
                  <Calendar
                    step={step}
                    selectedTime={selectedTime}
                    setSelectedTime={setSelectedTime}
                  />

                </Col>
              </Row>
            </section>

            <footer className="d-flex justify-content-between">
              <BackButton title="Previous" source={source} sourceId={sourceId} loading={loading} />

              <div>
                {step.attributes.actions.some((item) => item.action === 'cancel') && (
                <Button
                  onClick={onClickSessionCancel}
                  variant="outline-primary"
                  className={loading || !selectedTime ? 'disabled' : ''}
                >
                  Cancel Session
                </Button>
                )}
                {step?.attributes?.session_id && (
                <Button onClick={rescheduleSession} className={loading || !selectedTime ? 'disabled' : ''}>
                  {loading && <LoadingPlaceholder placement="inline" text=" " />}
                  Reschedule
                </Button>
                )}
                {!step?.attributes?.session_id
          && (
          <Button onClick={prepareSession} className={loading || !selectedTime ? 'disabled' : ''}>
            {loading && <LoadingPlaceholder placement="inline" text=" " />}
            Next
          </Button>
          )}
              </div>
            </footer>
          </div>
        )}
      <Outlet />
    </div>
  );
}
